import api from './base';
import i18n from '../../i18n';

export const sandboxApi = {
  execute: (code, language, token) =>
    api.post(
      '/sandbox/execute',
      { code, language },
      { headers: { Authorization: `Token ${token}` } }
    ),

  getHistory: (token) =>
    api.get('/sandbox/history', {
      headers: { Authorization: `Token ${token}` },
    }),

  saveSnippet: (snippetData, token) =>
    api.post('/sandbox/snippets', snippetData, {
      headers: { Authorization: `Token ${token}` },
    }),

  getSnippets: (token) =>
    api.get('/sandbox/snippets', {
      headers: { Authorization: `Token ${token}` },
    }),

  getSnippet: (snippetId, token) =>
    api.get(`/sandbox/snippets/${snippetId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateSnippet: (snippetId, snippetData, token) =>
    api.put(`/sandbox/snippets/${snippetId}`, snippetData, {
      headers: { Authorization: `Token ${token}` },
    }),

  deleteSnippet: (snippetId, token) =>
    api.delete(`/sandbox/snippets/${snippetId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Camunda Process Management
  startProcess: (prompt, userId, route, sandboxId, aiTutorId, token, options = {}) => {
    const currentLanguage = i18n.language;
    const sandbox_id = parseInt(sandboxId, 10);

    const data = {
      user_id: userId,
      sandbox: sandbox_id,
      prompt: prompt,
      language: currentLanguage,
      route: route,
      process_type: 'create_services',
      ai_tutor_id: aiTutorId
    };

    return api.post('/sandbox/start-process', data, {
      headers: { Authorization: `Token ${token}` },
    });
  },

  getProcessStage: (processId, token) =>
    api.get(`/sandbox/process/?id=${processId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Sandbox Management
  getSandboxes: (userId, role, token) => {
    let url = '/sandbox/sandbox/?archive=False';

    switch (role) {
      case 'owner':
        url += `&owner=${userId}`;
        break;
      case 'coworker':
        url += `&coworkers=${userId}`;
        break;
      case 'all':
        url += `&user=${userId}`;
        break;
      default:
        break;
    }

    return api.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
  },

  getActiveSandboxes: (userId, token) => {
    return api.get(`/sandbox/sandbox/?archive=False&user=${userId}`, {
      headers: { Authorization: `Token ${token}` },
    });
  },

  getSandbox: (sandboxId, token) =>
    api.get(`/sandbox/sandbox/${sandboxId}/`, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateSandboxName: (sandboxId, newName, token) =>
    api.patch(`/sandbox/sandbox/${sandboxId}/`,
      { name: newName },
      { headers: { Authorization: `Token ${token}` } }
    ),

  updateSandboxCoworkers: async (sandboxId, coworkerIds, token) => {
    // Get current access list
    const currentAccess = await api.get('/share/sandbox-access/', {
      params: { sandbox: sandboxId },
      headers: { Authorization: `Token ${token}` }
    });
    const currentUserIds = currentAccess
      .filter(access => access.user !== null)
      .map(access => access.user);

    // Remove users who are no longer in coworkerIds
    const usersToRemove = currentAccess.filter(
      access => access.user !== null && !coworkerIds.includes(access.user)
    );

    for (const access of usersToRemove) {
      await api.delete(`/share/sandbox-access/${access.id}/`, {
        headers: { Authorization: `Token ${token}` }
      });
    }

    // Add new users
    const usersToAdd = coworkerIds.filter(id => !currentUserIds.includes(id));

    for (const userId of usersToAdd) {
      await api.post('/share/sandbox-access/',
        { sandbox: sandboxId, user: userId },
        { headers: { Authorization: `Token ${token}` } }
      );
    }

    return true;
  },

  // Instance Management
  getInstances: (sandboxId, token) =>
    api.get(`/sandbox/instance/?sandbox=${sandboxId}&archive=False`, {
      headers: { Authorization: `Token ${token}` },
    }),

  getInstanceByProcessId: (processId, token) =>
    api.get('/sandbox/instance/', {
      params: { process: processId },
      headers: { Authorization: `Token ${token}` }
    }).then(data => data[0]),

  updateInstanceArchitecture: (instanceId, architecture, token) =>
    api.patch(`/sandbox/instance/${instanceId}/`,
      { architecture },
      { headers: { Authorization: `Token ${token}` } }
    ),

  // Component Management
  getComponents: (sandboxId, token) =>
    api.get(`/sandbox/component/?archive=False&sandbox=${sandboxId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  createComponent: (componentData, token) =>
    api.post('/sandbox/component/', componentData, {
      headers: { Authorization: `Token ${token}` }
    }),

  getComponentTypes: (token) =>
    api.get('/sandbox/component-type/', {
      headers: { Authorization: `Token ${token}` },
    }),

  executeSQLQuery: (componentId, query, token) =>
    api.post(`/sandbox/component/${componentId}/execute-sql/`,
      { query },
      { headers: { Authorization: `Token ${token}` } }
    ),

  // Component Relations
  getComponentsRelations: async (sandboxId, componentIds, token) => {
    const promises = componentIds.map(id =>
      api.get(`/sandbox/relation/?sandbox=${sandboxId}&from_component=${id}`, {
        headers: { Authorization: `Token ${token}` },
      })
    );
    const dataArrays = await Promise.all(promises);
    return dataArrays.flat();
  },

  // Environment Templates
  getEnvironmentTemplates: (token) =>
    api.get('/sandbox/environment-templates/', {
      headers: { Authorization: `Token ${token}` },
    }),

  createEnvironmentTemplate: (sandboxId, token) =>
    api.post('/sandbox/environment-templates/create_from_sandbox/',
      { sandbox_id: sandboxId },
      { headers: { Authorization: `Token ${token}` } }
    ),

  deployTemplate: (templateId, assignmentId, token) => {
    const data = assignmentId ? { assignment_id: assignmentId } : {};
    return api.post(`/sandbox/environment-templates/${templateId}/deploy/`,
      data,
      { headers: { Authorization: `Token ${token}` } }
    );
  },

  deleteTemplate: (templateId, token) =>
    api.delete(`/sandbox/environment-templates/${templateId}/`, {
      headers: { Authorization: `Token ${token}` },
    }),

  getTemplates: (token) =>
    api.get('/sandbox/environment-templates/', {
      headers: { Authorization: `Token ${token}` },
    }),

  getUserSandboxes: (token) =>
    api.get('/sandbox/sandbox/?archive=False', {
      headers: { Authorization: `Token ${token}` },
    }),

  // AI Assistant
  getAssistantResponse: (data, token) =>
    api.post('/sandbox/assistant-response', data, {
      headers: { Authorization: `Token ${token}` },
    }),
};
